import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../firebase';
import { authAPI } from '../utils/api';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .auth-root { font-family: 'Inter', sans-serif; min-height: 100vh; display: flex; background: #fff; -webkit-font-smoothing: antialiased; }

  .auth-left {
    flex: 1; background: linear-gradient(145deg, #1e3a8a 0%, #2563eb 50%, #7c3aed 100%);
    display: flex; flex-direction: column; justify-content: space-between;
    padding: 48px; position: relative; overflow: hidden;
  }
  .auth-left::before { content: ''; position: absolute; top: -120px; right: -120px; width: 400px; height: 400px; border-radius: 50%; background: rgba(255,255,255,0.06); pointer-events: none; }
  .auth-left::after { content: ''; position: absolute; bottom: -80px; left: -80px; width: 300px; height: 300px; border-radius: 50%; background: rgba(255,255,255,0.04); pointer-events: none; }
  .auth-left-content { position: relative; z-index: 1; }
  .auth-feature { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 22px; }
  .auth-feature-icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(255,255,255,0.12); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
  .auth-quote { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 20px 22px; position: relative; z-index: 1; }

  .auth-right { width: 520px; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; padding: 0; background: #fff; overflow-y: auto; }
  .auth-topbar { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 20px 40px; border-bottom: 1px solid #f1f5f9; flex-shrink: 0; }
  .auth-form-wrap { width: 100%; max-width: 380px; padding: 40px 0; }

  .auth-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
  .auth-input {
    width: 100%; padding: 11px 14px; border-radius: 10px;
    border: 1.5px solid #e2e8f0; font-size: 14px; font-family: 'Inter', sans-serif;
    color: #0f172a; background: #fff; outline: none; transition: border-color .2s, box-shadow .2s;
  }
  .auth-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
  .auth-input::placeholder { color: #94a3b8; }

  .auth-btn-primary {
    width: 100%; padding: 13px; border-radius: 10px; border: none; cursor: pointer;
    background: #2563eb; color: #fff; font-size: 15px; font-weight: 600;
    font-family: 'Inter', sans-serif; transition: all .2s;
    box-shadow: 0 2px 8px rgba(37,99,235,0.3);
  }
  .auth-btn-primary:hover:not(:disabled) { background: #1d4ed8; box-shadow: 0 4px 16px rgba(37,99,235,0.4); transform: translateY(-1px); }
  .auth-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  .auth-btn-google {
    width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px;
    padding: 11px 16px; background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px;
    font-size: 14px; font-weight: 600; color: #374151; cursor: pointer;
    font-family: 'Inter', sans-serif; transition: all .2s;
  }
  .auth-btn-google:hover:not(:disabled) { border-color: #bfdbfe; background: #f8faff; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
  .auth-btn-google:disabled { opacity: 0.6; cursor: not-allowed; }

  .auth-divider { display: flex; align-items: center; gap: 12px; margin: 18px 0; }
  .auth-divider-line { flex: 1; height: 1px; background: #e2e8f0; }
  .auth-error { padding: 11px 14px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; color: #dc2626; font-size: 13px; margin-bottom: 16px; }
  .auth-success { padding: 11px 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; color: #059669; font-size: 13px; }

  .auth-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  @media (max-width: 900px) {
    .auth-left { display: none !important; }
    .auth-right { width: 100% !important; }
    .auth-topbar { padding: 16px 20px !important; }
    .auth-form-wrap { padding: 32px 24px !important; }
    .auth-grid-2 { grid-template-columns: 1fr !important; }
  }
`;

export default function Register() {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) return setError('Passwords do not match.');
    if (form.password.length < 6) return setError('Password must be at least 6 characters.');
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(auth, form.email, form.password);
      if (form.name) await updateProfile(result.user, { displayName: form.name });
      authAPI.sendWelcome().catch(() => {});
      await signOut(auth);
      setSent(true);
    } catch (err) {
      setError({
        'auth/email-already-in-use': 'This email is already registered.',
        'auth/invalid-email': 'Invalid email address.',
        'auth/weak-password': 'Password is too weak.',
      }[err.code] || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      navigate('/dashboard');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') setError('Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const LeftPanel = () => (
    <div className="auth-left">
      <div className="auth-left-content">
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 56 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#fff', fontWeight: 800 }}>⊛</div>
          <span style={{ fontWeight: 800, fontSize: 18, color: '#fff', letterSpacing: '-0.4px' }}>ClickSaver</span>
        </Link>

        <h2 style={{ fontSize: 32, fontWeight: 900, color: '#fff', letterSpacing: '-1px', lineHeight: 1.2, marginBottom: 12 }}>
          Start protecting<br />your ad spend.
        </h2>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginBottom: 48, maxWidth: 340 }}>
          Join hundreds of advertisers who save 15–25% on their Google Ads every month.
        </p>

        {[
          { icon: '🎁', title: 'No lock-in contracts', desc: 'Cancel anytime, no questions asked.' },
          { icon: '⚡', title: 'Live in 5 minutes', desc: 'Add one script tag and you\'re protected.' },
          { icon: '💰', title: 'Average $1,200/mo saved', desc: 'Real ROI from day one.' },
        ].map((f, i) => (
          <div key={i} className="auth-feature">
            <div className="auth-feature-icon">{f.icon}</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 3 }}>{f.title}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="auth-quote">
        <div style={{ display: 'flex', gap: 2, marginBottom: 10 }}>
          {[...Array(5)].map((_, i) => <span key={i} style={{ color: '#fbbf24', fontSize: 13 }}>★</span>)}
        </div>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, marginBottom: 12, fontStyle: 'italic' }}>
          "Setup took less than 5 minutes. Within 24 hours we could see fraudulent IPs being blocked."
        </p>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>— Sarah K., Digital Marketing Director</div>
      </div>
    </div>
  );

  if (sent) {
    return (
      <div className="auth-root">
        <style>{CSS}</style>
        <LeftPanel />
        <div className="auth-right">
          <div className="auth-topbar">
            <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 9 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 800 }}>⊛</div>
              <span style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', letterSpacing: '-0.4px' }}>ClickSaver</span>
            </Link>
            <Link to="/" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', fontWeight: 500 }}>← Back to website</Link>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '0 56px' }}>
          <div className="auth-form-wrap" style={{ textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: 20, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 24px' }}>✉️</div>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 10, letterSpacing: '-0.5px' }}>Account created!</h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 20 }}>
              Welcome aboard! We've sent a welcome email to <strong style={{ color: '#0f172a' }}>{form.email}</strong> with everything you need to get started.
            </p>
            <div className="auth-success" style={{ marginBottom: 28, textAlign: 'left' }}>
              ✓ Your account is ready — sign in to access your dashboard.
            </div>
            <Link to="/login" className="auth-btn-primary" style={{ display: 'block', textDecoration: 'none', textAlign: 'center' }}>
              Go to Sign In →
            </Link>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-root">
      <style>{CSS}</style>
      <LeftPanel />

      {/* Right panel */}
      <div className="auth-right">
        {/* Top bar */}
        <div className="auth-topbar">
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 800 }}>⊛</div>
            <span style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', letterSpacing: '-0.4px' }}>ClickSaver</span>
          </Link>
          <Link to="/" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
            ← Back to website
          </Link>
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '0 56px' }}>
        <div className="auth-form-wrap">
          {/* Trial badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f0fdf4', color: '#059669', fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 40, border: '1px solid #bbf7d0', marginBottom: 24 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
            No lock-in contracts — cancel anytime
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', marginBottom: 6 }}>Create your account</h1>
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
            Already have an account? <Link to="/login" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
          </p>

          {/* Google */}
          <button className="auth-btn-google" onClick={handleGoogle} disabled={loading}>
            <GoogleIcon /> Continue with Google
          </button>

          <div className="auth-divider">
            <div className="auth-divider-line" />
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500, whiteSpace: 'nowrap' }}>or register with email</span>
            <div className="auth-divider-line" />
          </div>

          {/* Form */}
          <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="auth-grid-2">
              <div>
                <label className="auth-label">Full name</label>
                <input className="auth-input" placeholder="Jane Smith" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="auth-label">Email address</label>
                <input className="auth-input" type="email" placeholder="jane@company.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
            </div>
            <div className="auth-grid-2">
              <div>
                <label className="auth-label">Password</label>
                <input className="auth-input" type="password" placeholder="Min. 6 characters" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
              </div>
              <div>
                <label className="auth-label">Confirm password</label>
                <input className="auth-input" type="password" placeholder="••••••••" value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
              </div>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <button className="auth-btn-primary" type="submit" disabled={loading} style={{ marginTop: 4 }}>
              {loading ? 'Creating account…' : 'Create Account →'}
            </button>
          </form>

          <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', marginTop: 20, lineHeight: 1.6 }}>
            By creating an account you agree to our{' '}
            <Link to="/terms-policy" style={{ color: '#64748b', textDecoration: 'underline' }}>Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" style={{ color: '#64748b', textDecoration: 'underline' }}>Privacy Policy</Link>.
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
